/**
 * Drive-based board access (eval/MIRO_PLAN.md "Drive-based access"): types and
 * REST calls shared by both board apps. Every board can live in a Google Drive
 * folder as "<title>.board.pdf"; the folder's sharing decides who else opens
 * it (writer → edit, commenter / reader → view). Share links are off by
 * default and view-only.
 */

import type { ApiClient } from './api';

export type BoardRole = 'owner' | 'editor' | 'commenter' | 'viewer';

export interface BoardDriveStatus {
  folder_id: string | null;
  folder_name: string | null;
  folder_url: string | null;
  file_id: string | null;
  file_name: string | null;
  file_url: string | null;
  synced_at: string | null;
  error: string | null;
  /** Owner of a board not yet in Drive: show the one-time "Choose folder" prompt. */
  needs_folder: boolean;
  /** View-only share link on. */
  share_enabled: boolean;
  /** Owner only, while the link is on. */
  share_token?: string | null;
}

export interface DriveFolder {
  id: string;
  name: string;
  parent_id?: string | null;
  path?: string;
  can_add: boolean;
  shared_drive?: boolean;
  /** One of the user's boards already lives here. */
  used?: boolean;
  /** Search rows: where the folder lives ("Job › 01-Inbox"). */
  location?: string;
}

/** One step of a folder's real path, top of the picker first. */
export interface DriveCrumb {
  id: string;
  name: string;
}

export interface BoardAccessRow {
  user_id: string;
  user_name: string;
  user_email: string;
  role: BoardRole;
  via: 'owner' | 'member' | 'link' | 'drive';
}

const json = (method: string, data: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(data),
});

export function boardDriveApi(client: ApiClient) {
  return {
    folders(opts: { parent?: string | null; q?: string } = {}): Promise<{ folders: DriveFolder[]; recent: DriveFolder[] }> {
      const p = new URLSearchParams();
      if (opts.parent) p.set('parent', opts.parent);
      if (opts.q) p.set('q', opts.q);
      const qs = p.toString();
      return client.json(`/api/boards/drive/folders/${qs ? `?${qs}` : ''}`);
    },
    /** A folder's real path (shared drive first; My Drive is the picker's root). */
    trail(folderId: string): Promise<{ trail: DriveCrumb[] }> {
      return client.json(`/api/boards/drive/folders/?trail=${encodeURIComponent(folderId)}`);
    },
    status(boardId: string): Promise<BoardDriveStatus> {
      return client.json(`/api/boards/${boardId}/drive/`);
    },
    setFolder(boardId: string, folderId: string): Promise<BoardDriveStatus> {
      return client.json(`/api/boards/${boardId}/drive/`, json('POST', { folder_id: folderId }));
    },
    shareOn(boardId: string, rotate = false): Promise<{ token: string; url: string; enabled: boolean }> {
      return client.json(`/api/boards/${boardId}/share/${rotate ? '?rotate=1' : ''}`, { method: 'POST' });
    },
    shareOff(boardId: string): Promise<{ enabled: boolean }> {
      return client.json(`/api/boards/${boardId}/share/`, { method: 'DELETE' });
    },
    access(boardId: string): Promise<{ members: BoardAccessRow[] }> {
      return client.json(`/api/boards/${boardId}/members/`);
    },
    /** Full-size board image; the server wraps it into the Drive anchor PDF (debounced). */
    async uploadSnapshot(boardId: string, image: Blob): Promise<void> {
      const fd = new FormData();
      fd.append('file', image, 'snapshot.png');
      await client.json(`/api/boards/${boardId}/snapshot/`, { method: 'PUT', body: fd });
    },
  };
}

export type BoardDriveApi = ReturnType<typeof boardDriveApi>;

/** Roles that may change the canvas. */
export function canEditBoard(role: BoardRole | null | undefined): boolean {
  return role === 'owner' || role === 'editor';
}

export function roleLabel(role: BoardRole): string {
  return { owner: 'Owner', editor: 'Can edit', commenter: 'Can view (Drive commenter)', viewer: 'Can view' }[role];
}

/**
 * Snapshot throttle: upload when the scene changed and at most every
 * `minGapMs`, so the Drive copy follows the board without a request per edit.
 */
export class SnapshotThrottle {
  private lastHash: number | null = null;
  private lastAt = 0;
  constructor(private readonly minGapMs = 120_000, private readonly now: () => number = Date.now) {}
  due(hash: number, force = false): boolean {
    if (hash === this.lastHash) return false;
    if (!force && this.lastHash !== null && this.now() - this.lastAt < this.minGapMs) return false;
    return true;
  }
  mark(hash: number): void {
    this.lastHash = hash;
    this.lastAt = this.now();
  }
  reset(): void {
    this.lastHash = null;
  }
}
